import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Icon,
  Label,
  Button,
  Tab,
  TabItem,
  Menu,
  MenuItem,
  Tooltip,
  Utils,
} from 'mw-style-react';
import cn from 'classnames';
import { SET_MODAL } from 'constants';
import AppUtils from '@control-front-end/utils/utils';
import useIntl from 'useIntl';
import mes from './intl';
import './ScriptHeader.scss';

function ScriptHeader(props) {
  const {
    accId,
    activeScript,
    activeEnv,
    readOnly,
    hasGraph,
    handleDeploy,
    handleShareScript,
    handleChangeEnv,
    handleUpdateScript,
    handleRemoveScript,
    handleOpenScriptGraph,
  } = props;
  const t = useIntl();
  const dispatch = useDispatch();
  const config = useSelector((state) => state.config);
  const [menu, toggleMenu] = useState(false);
  const [subMenu, setSubMenu] = useState(null);
  const canRemove = activeScript.privs && activeScript.privs.remove;

  /**
   * Показать модалку для создания приложения CDU
   */
  const showEditModal = () => {
    dispatch({
      type: SET_MODAL,
      payload: {
        name: 'CreateScriptApp',
        data: activeScript,
        closeConfirm: true,
        callback: (data) => handleUpdateScript(data),
      },
    });
  };

  /**
   * Обработка клика в меню
   */
  const handleClickMenu = ({ value }) => {
    switch (value) {
      case 'edit':
        showEditModal();
        toggleMenu(false);
        break;
      case 'remove':
        handleRemoveScript();
        toggleMenu(false);
        break;
      case 'corezoid':
        setSubMenu(!subMenu ? 'corezoid' : null);
        break;
      default:
        break;
    }
  };

  /**
   * Клик по операции с коннектором
   */
  const handleOpenCorezoidProc = ({ value }) => {
    const { companyId, procId } = value.corezoidCredentials;
    const url = `${config.adminCorezoid}/${companyId}/process/${procId}`;
    window.open(url);
    setSubMenu(null);
    toggleMenu(!menu);
  };

  /**
   * Подменю для процессов Corezoid
   */
  const renderCorezoidMenu = () => (
    <div
      styleName="se__header__title__menu"
      onClick={(e) => e.stopPropagation()}
    >
      <Menu onClick={handleOpenCorezoidProc} onClose={() => setSubMenu(null)}>
        {activeScript.envs.map((env) => (
          <MenuItem
            key={env.id}
            styleName="se__header__title__menu__subitem"
            label={Utils.toUpperLatter(env.title)}
            rightIcon="link_external"
            value={env}
          />
        ))}
      </Menu>
    </div>
  );

  /**
   * Отрисовать меню для скрипта
   */
  const renderMenu = () => (
    <div
      styleName={cn('se__header__title__menu', { active: !!menu })}
      onClick={(e) => e.stopPropagation()}
    >
      <Menu
        onClick={handleClickMenu}
        onClose={() => {
          toggleMenu(!menu);
          if (subMenu) setSubMenu(null);
        }}
      >
        <MenuItem leftIcon="edit" label={t(mes.editCredentials)} value="edit" />
        <MenuItem
          styleName="se__header__title__menu__compound"
          label={t(mes.openProcess)}
          value="corezoid"
          leftIcon="process"
          rightIcon="arrow"
          onClickRightIcon={() => setSubMenu(!subMenu ? 'corezoid' : null)}
        >
          {subMenu ? renderCorezoidMenu() : null}
        </MenuItem>
        <MenuItem
          leftIcon="trash"
          label={t(mes.remove)}
          value="remove"
          visibility={canRemove ? 'visible' : 'disabled'}
        />
      </Menu>
    </div>
  );

  const renderHeaderActions = () => (
    <div styleName="se__header__actions">
      <Tooltip value={t(mes.graph)}>
        <Button
          icon="graph"
          size="smallplus"
          type="tertiary"
          rounded={true}
          onClick={handleOpenScriptGraph}
          visibility={hasGraph ? 'visible' : 'hidden'}
        />
      </Tooltip>
      <Tooltip value={t(mes.shareScript)}>
        <Button
          icon="person_add"
          size="smallplus"
          type="tertiary"
          rounded={true}
          onClick={handleShareScript}
        />
      </Tooltip>
      <Tooltip value={t(mes.launchScript)}>
        <Button
          styleName="se__header__actions__run"
          icon="dropdown"
          size="smallplus"
          type="tertiary"
          rounded={true}
          onClick={() => {
            const action =
              activeScript.data.sharedWith &&
              activeScript.data.sharedWith[0].value === 'anyone'
                ? 'view_public'
                : 'view';
            const url = AppUtils.makeUrl(
              `/script/${accId}/${action}/${activeScript.ref}/${activeEnv.title}/index`
            );
            window.open(url);
          }}
        />
      </Tooltip>
      <Button
        size="smallplus"
        fontWeight="normal"
        label={t(mes.deploy)}
        onClick={handleDeploy}
        visibility={readOnly ? 'disabled' : 'visible'}
      />
    </div>
  );

  return (
    <div styleName="se__header">
      <div styleName="se__header__title">
        <Label
          fontWeight="semibold"
          value={t(mes.smartForms)}
          onClick={() => {
            document.location.href = AppUtils.makeUrl(
              `/actors_graph/${accId}/scripts`
            );
          }}
        />
        <Icon type="arrow" size="small" />
        <div styleName="se__header__title__script__name">
          <Label fontWeight="semibold" value={activeScript.title} />
        </div>
        <div styleName="se__header__title__menu__wrap">
          <Icon
            type="settings"
            size="small"
            onClick={() => toggleMenu(!menu)}
          />
          {menu ? renderMenu() : null}
        </div>
      </div>
      <div styleName="se__header__tabs">
        <Tab
          value={activeEnv}
          type="auto"
          underline={false}
          onChange={handleChangeEnv}
        >
          {activeScript.envs.map((env) => (
            <TabItem
              key={env.id}
              label={Utils.toUpperLatter(env.title)}
              value={env}
            />
          ))}
        </Tab>
      </div>
      {renderHeaderActions()}
    </div>
  );
}

ScriptHeader.propTypes = {
  accId: PropTypes.string,
  activeScript: PropTypes.object,
  activeEnv: PropTypes.object,
  readOnly: PropTypes.bool,
  hasGraph: PropTypes.bool,
  handleDeploy: PropTypes.func,
  handleShareScript: PropTypes.func,
  handleRemoveScript: PropTypes.func,
  handleChangeEnv: PropTypes.func,
  handleUpdateScript: PropTypes.func,
  handleOpenScriptGraph: PropTypes.func,
};

export default ScriptHeader;
